/**
 * JWT 토큰 유틸리티
 * JWT 토큰을 디코딩하여 payload 정보를 추출
 */

/**
 * JWT 토큰의 payload를 디코딩
 * @param token JWT 토큰 문자열
 * @returns 디코딩된 payload 객체 또는 null
 */
export const decodeJwt = (token: string): any | null => {
  try {
    // JWT는 header.payload.signature 형식
    const parts = token.split('.')
    if (parts.length !== 3) {
      console.error('Invalid JWT token format')
      return null
    }

    // payload 부분 디코딩 (base64url)
    const payload = parts[1]
    // base64url을 base64로 변환 (URL-safe 문자를 일반 base64 문자로)
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    // padding 추가
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    
    // 디코딩
    const decoded = atob(padded)
    return JSON.parse(decoded)
  } catch (error) {
    console.error('JWT 디코딩 실패:', error)
    return null
  }
}

/**
 * JWT 토큰에서 email 추출
 * @param token JWT 토큰 문자열
 * @returns email 문자열 또는 null
 */
export const getEmailFromToken = (token: string): string | null => {
  const payload = decodeJwt(token)
  if (!payload) {
    return null
  }
  
  // JWT payload에서 email 필드 추출
  // 일반적으로 'email' 또는 'sub' (subject)에 email이 포함될 수 있음
  return payload.email || payload.sub || null
}

/**
 * JWT 토큰에서 특정 필드 추출
 * @param token JWT 토큰 문자열
 * @param fieldName 추출할 필드 이름
 * @returns 필드 값 또는 null
 */
export const getFieldFromToken = (token: string, fieldName: string): any | null => {
  const payload = decodeJwt(token)
  if (!payload) {
    return null
  }
  
  return payload[fieldName] || null
}

/**
 * JWT 토큰에서 role 추출
 * @param token JWT 토큰 문자열
 * @returns role 문자열 또는 null
 */
export const getRoleFromToken = (token: string): string | null => {
  return getFieldFromToken(token, 'role')
}

/**
 * JWT 만료 여부 확인 (exp는 초 단위)
 * @param token JWT 토큰 문자열
 * @param nowMs 현재 시각(ms). 생략 시 Date.now() 사용
 * @returns 만료되었으면 true
 */
export const isTokenExpired = (token: string, nowMs?: number): boolean => {
  const payload = decodeJwt(token)
  if (!payload || typeof payload.exp !== 'number') return true
  const now = nowMs ?? Date.now()
  // exp는 초 단위, 60초 여유 두고 만료로 간주
  return payload.exp * 1000 < now - 60 * 1000
}

